// src/components/ThankYou.js
import React, { useEffect } from 'react';
import { Container, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useToken from '../../hooks/useToken';
import useStudyTiming from '../../hooks/useStudyTiming';
import useProgressRedirect from '../../hooks/useProgressRedirect'; // <== If you use it everywhere
import ProgressTracker from '../shared/ProgressTracker';
import './Dashboard.css'; // ✅ Reuse the same CSS

function ThankYou() {
  const { token, refreshAccessToken, removeToken, getValidToken } = useToken();
  const timing = useStudyTiming();
  const navigate = useNavigate();

  useProgressRedirect("END"); // Optional if you want to keep redirection consistency

  useEffect(() => {
    const checkLogin = async () => {
      const authToken = await getValidToken();
      if (!authToken) {
        removeToken();
        navigate("/", { replace: true });
      }
    };
    checkLogin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container className="dashboard-container py-5">
      <ProgressTracker currentStep="end" />
      <Card className="shadow-lg border-0 mb-4">
        <Card.Body>
          <h2 className="mb-4 text-center">🎉 Thank You for Participating!</h2>
          <p className="text-center text-muted">
            Your contributions are invaluable to our research. Your testing results, questionnaire responses, and feedback will help shape better security practices and tools in the future.
          </p>
          <div className="text-center">
            <Button variant="outline-primary" onClick={() => navigate('/findings-summary')}>
              📄 View My Submitted Findings
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card className="shadow-lg border-0">
        <Card.Body>
          <h3 className="mb-3">📋 Debriefing</h3>
          
          <div className="text-start">
            <h5 className="mt-4 mb-3">Study Purpose</h5>
            <p>
              This study investigates whether providing <strong>comprehensive design documentation</strong> to penetration testers 
              improves their <strong>effectiveness</strong> and <strong>efficiency</strong> during security assessments. We are 
              interested in understanding how different levels of documentation support impact the testing process and outcomes.
            </p>

            <h5 className="mt-4 mb-3">Research Objectives</h5>
            <ul>
              <li>Evaluate whether enhanced documentation improves the <strong>effectiveness</strong> and <strong>efficiency</strong> of penetration testing.</li>
              <li>Analyze how testers' <strong>background characteristics</strong> (e.g., years of experience, familiarity with design artifacts) influence the benefits of documentation.</li>
              <li>Understand <strong>how penetration testers perceive and interact with different types of documentation</strong> in practice.</li>
            </ul>

            <h5 className="mt-4 mb-3">Experimental Design</h5>
            <p>
              Participants were randomly assigned to one of two conditions:
            </p>
            <ul>
              <li><strong>With enhanced documentation:</strong> Received comprehensive design artifacts including business models, use case diagrams, C4 diagrams (context, container, component), and additional security-focused documentation.</li>
              <li><strong>With standard documentation:</strong> Received standard design artifacts (business models, use case diagrams, C4 diagrams) without the additional security-focused materials.</li>
            </ul>
            <p>
              Each participant assessed one vulnerable website for {timing.mandatory ? `${timing.mandatory} minutes (with an optional ${timing.extra}-minute extension)` : 'a fixed testing period (with an optional extension)'} and completed pre- and post-experiment surveys. 
              This between-subjects design allows us to compare outcomes across conditions while controlling for individual differences.
            </p>

            <h5 className="mt-4 mb-3">Why Different Conditions?</h5>
            <p>
              The specific research question and the nature of the "enhanced documentation" were not disclosed beforehand to avoid bias. 
              By comparing results between conditions, we can objectively measure the impact of different documentation approaches on penetration testing practices.
            </p>

            <h5 className="mt-4 mb-3">Follow-Up Interview</h5>
            <p>
              Participants who volunteered for the optional post-study interview will be contacted via email to discuss their experience in greater depth 
              and provide additional insights into their testing approach and documentation usage.
            </p>

            <h5 className="mt-4 mb-3">Contact Information</h5>
            <p>
              If you have any questions about the study, your participation, or the results, please feel free to contact the research team:
            </p>
            <p className="mb-2">
              <strong>Research Team:</strong> DistriNet Research Group, KU Leuven<br/>
              <strong>Email:</strong> <a href="mailto:anhduy.tran@kuleuven.be">anhduy.tran@kuleuven.be</a>
            </p>

            <div className="alert alert-info mt-4">
              <strong>Thank you again for your valuable contribution to this research!</strong> Your participation helps advance our understanding of effective security practices.
            </div>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default ThankYou;
